import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';

async function writeClipboard(text: string) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback para http/iframes sem Clipboard API.
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(el);
  if (!ok) throw new Error('copy failed');
}

/** Cópia sem toast/estado (para fluxos que montam a própria mensagem). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await writeClipboard(text);
    return true;
  } catch {
    return false;
  }
}

export interface CopyOptions {
  /** Mensagem do toast; `false` para não mostrar toast (só o ícone muda). */
  message?: string | false;
}

/**
 * Cópia padronizada: grava na área de transferência, marca `copiedKey` por 2s (para trocar
 * o ícone por Check) e mostra o toast "Copiado!". Use `key` quando a tela tem vários botões.
 */
export function useCopyToClipboard(resetMs = 2000) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(
    async (text: string, key = 'default', options: CopyOptions = {}) => {
      try {
        await writeClipboard(text);
        setCopiedKey(key);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopiedKey(null), resetMs);
        if (options.message !== false) {
          showSuccess(t('ui.copied'), options.message || t('ui.copiedMessage'));
        }
        return true;
      } catch {
        showError(t('common.error'), t('ui.copyError'));
        return false;
      }
    },
    [resetMs, showError, showSuccess, t]
  );

  const isCopied = useCallback((key = 'default') => copiedKey === key, [copiedKey]);

  return { copy, copiedKey, isCopied };
}
